import * as React from "react";

import { fuzzyMatchIndices, fuzzyMatchIndicesPartial } from "./fuzzyFilter";

export interface FuzzyHighlightProps {
  /** Full text to match the query against. */
  text: string;
  /** Fuzzy query string. */
  query: string;
  /** When true, use partial matching — highlights however many query characters
   *  can be consumed by `text` even when `text` is too short for the full query.
   *  Useful for nodes whose path only covers a prefix of the query. */
  partial?: boolean;
  /** Optional `[start, end)` range — only characters within this slice of
   *  `text` are rendered, but matching runs against the full `text`. Useful
   *  for rendering just the label segment of a dotted path. */
  slice?: [number, number];
  /** Class applied to each `<mark>` wrapping matched characters. Omit to fall
   *  back to the browser's default highlight styling. */
  markClassName?: string;
}

/**
 * Renders `text` (or a slice of it) with the characters matched by `query`
 * wrapped in a `<mark>`. When `query` is empty or produces no match indices the
 * plain text is returned. Shared by the metrics filter tree and the Session
 * Metrics tab so both highlight fuzzy matches identically.
 */
export function FuzzyHighlight({
  text,
  query,
  partial,
  slice,
  markClassName,
}: FuzzyHighlightProps) {
  const indices = React.useMemo(() => {
    if (query.trim() !== "") {
      return partial
        ? fuzzyMatchIndicesPartial(query, text)
        : fuzzyMatchIndices(query, text);
    } else {
      return null;
    }
  }, [query, text, partial]);

  const start = slice ? slice[0] : 0;
  const end = slice ? slice[1] : text.length;
  const display = text.slice(start, end);

  if (!indices || indices.length === 0) {
    return <>{display}</>;
  }

  const matchSet = new Set(indices);
  const parts: React.ReactNode[] = [];
  let run = "";
  let inMatch = false;

  for (let i = start; i < end; i++) {
    const isMatch = matchSet.has(i);
    if (isMatch !== inMatch && run.length > 0) {
      parts.push(
        inMatch ? (
          <mark key={parts.length} className={markClassName}>
            {run}
          </mark>
        ) : (
          <span key={parts.length}>{run}</span>
        ),
      );
      run = "";
    }
    run += text[i];
    inMatch = isMatch;
  }

  if (run.length > 0) {
    parts.push(
      inMatch ? (
        <mark key={parts.length} className={markClassName}>
          {run}
        </mark>
      ) : (
        <span key={parts.length}>{run}</span>
      ),
    );
  }

  return <>{parts}</>;
}
